/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { useState, type SyntheticEvent } from 'react';
import type { WebLink } from '@jira/issue__weblinks/src/WebLinks.types.tsx';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { FilmstripView, type ScrollEvent } from '@atlaskit/media-filmstrip';
import { Box, xcss } from '@atlaskit/primitives';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import DeleteModal from './delete-modal/index.tsx';
import LoomVideoCard from './loom-video-card/index.tsx';

type WebLinkId = string;

type Props = {
	links: WebLink[];
	animate: boolean;
	offset: number;
	onScroll: (e: ScrollEvent) => void;
	onDeleteLink: (id: WebLinkId) => void;
};

export const LoomVideosList = ({ links, animate, offset, onScroll, onDeleteLink }: Props) => {
	const [pendingDeleteId, setPendingDeleteId] = useState<WebLinkId | null>(null);

	if (links.length === 0) {
		return null;
	}

	const handleDeleteLink = (id: WebLinkId) => {
		setPendingDeleteId(id);
	};

	const handleDeleteConfirm = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		if (pendingDeleteId !== null) {
			onDeleteLink(pendingDeleteId);
		}
		setPendingDeleteId(null);
		fireUIAnalytics(analyticsEvent, 'loom-video-lists-confirm-modal-delete-video');
	};

	const handleDeleteCancel = (
		_: SyntheticEvent | KeyboardEvent,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		setPendingDeleteId(null);
		fireUIAnalytics(analyticsEvent, 'loom-video-lists-confirm-modal-cancel-delete-video');
	};

	// when there is only a single video, make it fill the available
	const videoCards =
		links.length === 1 ? (
			<Box
				testId="issue-view-base.content.loom-videos.ui.co-use.loom-videos-list.single"
				xcss={singleCardStyles}
			>
				<LoomVideoCard link={links[0]} onDeleteLink={handleDeleteLink} />
			</Box>
		) : (
			<FilmstripView onScroll={onScroll} animate={animate} offset={offset}>
				{links
					.slice()
					.reverse() // we want the leftmost video to be the most recent one
					.map((l) => (
						<Box
							key={l.id}
							testId="issue-view-base.content.loom-videos.ui.co-use.loom-videos-list.multi"
							xcss={multiCardStyles}
						>
							<LoomVideoCard link={l} onDeleteLink={handleDeleteLink} />
						</Box>
					))}
			</FilmstripView>
		);

	return (
		<>
			{videoCards}
			{pendingDeleteId !== null && (
				<DeleteModal onConfirm={handleDeleteConfirm} onCancel={handleDeleteCancel} />
			)}
		</>
	);
};

const singleCardStyles = xcss({
	maxWidth: '700px',
	minWidth: '380px',
	aspectRatio: '1.57894736842',
	marginBottom: 'space.100',
});

const multiCardStyles = xcss({
	width: '380px',
	height: '240px',
	marginBottom: 'space.100',
});
